import { Link, useParams } from 'react-router-dom';
import { useSiteData } from '../../context/SiteDataContext';
import { useMetaTags } from '../../utils/useMetaTags';
import { getAuthorSlug } from '../../utils/author';

const formatDate = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

export default function AuthorPage() {
  const { slug } = useParams();
  const { authors, blogPosts, settings } = useSiteData();

  const author = authors.find((item) => getAuthorSlug(item) === slug) || null;
  const authoredPosts = blogPosts.filter((post) => {
    if (!author) return false;
    return post.authorId === author.id || post.author?.toLowerCase() === author.name?.toLowerCase();
  });

  useMetaTags({
    title: author ? `${author.name} | Primecrest Enterprise LTD` : 'Author Profile | Primecrest Enterprise LTD',
    description: author?.bio || `Explore articles and insights published by ${author?.name || 'Primecrest Enterprise'} on the Primecrest Enterprise website.`,
    image: author?.imageUrl || settings.logoUrl,
    type: 'profile',
  });

  if (!author) {
    return (
      <main className="pt-24 min-h-screen bg-gray-50 px-6 py-20">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl font-black text-gray-900">Author profile not found</h1>
          <p className="text-gray-600 mt-3">The requested author profile is not available right now.</p>
          <Link to="/blog" className="inline-flex mt-6 items-center gap-2 rounded-full bg-orange-600 px-5 py-3 text-white font-semibold">
            <i className="ri-arrow-left-line" /> Back to blog
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-24 min-h-screen bg-white">
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-orange-700 text-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-8 px-6 py-20 lg:flex-row lg:items-center">
          <div className="flex h-32 w-32 shrink-0 items-center justify-center overflow-hidden rounded-full border-4 border-white/70 bg-white/10 shadow-2xl lg:h-40 lg:w-40">
            {author.imageUrl ? (
              <img src={author.imageUrl} alt={author.name} className="h-full w-full object-cover" loading="lazy" />
            ) : (
              <span className="text-4xl font-black">{author.name?.[0]?.toUpperCase() || 'A'}</span>
            )}
          </div>
          <div className="max-w-2xl">
            <p className="text-sm uppercase tracking-[0.3em] text-orange-200">Primecrest Author</p>
            <h1 className="mt-3 text-4xl font-black sm:text-5xl">{author.name}</h1>
            {author.subtitle && <p className="mt-3 text-lg text-slate-200">{author.subtitle}</p>}
            <div className="mt-6 flex flex-wrap gap-3 text-sm text-slate-100">
              {author.joinDate && <span className="rounded-full bg-white/10 px-3 py-2">Joined {author.joinDate}</span>}
              {author.lastActive && <span className="rounded-full bg-white/10 px-3 py-2">Last active {author.lastActive}</span>}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-8 px-6 py-16 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
          <h2 className="text-2xl font-black text-gray-900">About this author</h2>
          <div className="prose prose-lg mt-6 max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: author.bio || '<p>More information about this author will be shared soon.</p>' }} />
        </div>

        <aside className="space-y-6 rounded-3xl border border-gray-200 bg-gray-50 p-8">
          <div>
            <h3 className="text-lg font-black text-gray-900">Professional links</h3>
            <div className="mt-4 space-y-3 text-sm text-gray-700">
              {author.linkedIn && (
                <a href={author.linkedIn} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-full bg-white px-4 py-3 shadow-sm hover:bg-orange-50">
                  <i className="ri-linkedin-fill text-orange-600" /> LinkedIn profile
                </a>
              )}
              {author.upwork && (
                <a href={author.upwork} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-full bg-white px-4 py-3 shadow-sm hover:bg-orange-50">
                  <i className="ri-briefcase-fill text-orange-600" /> Upwork profile
                </a>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-black text-gray-900">Articles by {author.name}</h3>
            <div className="mt-4 space-y-3">
              {authoredPosts.length > 0 ? authoredPosts.map((post) => (
                <Link key={post.id} to={`/blog/${post.id}`} className="block rounded-2xl border border-gray-200 bg-white p-4 hover:border-orange-300 hover:bg-orange-50">
                  <p className="text-sm font-semibold text-gray-900">{post.title}</p>
                  <p className="mt-1 text-xs text-gray-500">{formatDate(post.date)}</p>
                </Link>
              )) : (
                <p className="text-sm text-gray-600">No published articles yet.</p>
              )}
            </div>
          </div>
        </aside>
      </section>
    </main>
  );
}
